import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { AppShell, Button, Center, Group, Image, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { AppHeader } from './AppHeader';
import logoURL from '../assets/bio-rad-logo.svg';
import { apiRequest, loginRequest, logoutRequest } from '../auth/azureConfig';
import { isUserAuthorized } from '../auth/groupAuthorization';
import { setAccessToken } from '../generated/baseApi';
import { resetAntibodyAnalysis } from '../store/slices/antibodyAnalysis.slice';
import { sessionExpired, sessionRecovered } from '../store/slices/auth.slice';
import { resetClonotypes } from '../store/slices/clonotypes.slice';
import { persistor } from '../store/store';
import type { AppDispatch, AppState } from '../store/store';

export function AppWithAzureAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch<AppDispatch>();
  const isLoading = inProgress !== InteractionStatus.None;
  const account = accounts[0];
  const [tokenReady, setTokenReady] = React.useState(false);
  const [localMode, setLocalMode] = React.useState(() => localStorage.getItem('localMode') === 'true');
  const [localUser, setLocalUser] = React.useState<{ name: string; username: string } | null>(() => {
    try {
      const stored = localStorage.getItem('localUser');
      return stored ? (JSON.parse(stored) as { name: string; username: string }) : null;
    } catch (err) {
      console.warn('Failed to restore local user from localStorage — clearing invalid entry', err);
      localStorage.removeItem('localUser');
      return null;
    }
  });
  const [loadingMessage, setLoadingMessage] = React.useState('Loading...');
  // Local-mode users bypass the Azure group gate; everyone else must be in the required group.
  const isAuthorized = localMode || isUserAuthorized(account?.idTokenClaims);

  // Re-activate local mode token on browser restart (localMode persisted in localStorage)
  React.useEffect(() => {
    if (localMode) {
      setAccessToken('LOCAL_DEV_MODE');
      setTokenReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update loading message based on MSAL state
  React.useEffect(() => {
    if (inProgress === InteractionStatus.Login) {
      setLoadingMessage('Signing in...');
    } else if (inProgress === InteractionStatus.Logout) {
      setLoadingMessage('Signing out...');
    } else if (accounts.length > 0 && !tokenReady) {
      setLoadingMessage('Loading...');
    }
  }, [inProgress, accounts?.length, tokenReady]);

  // Check if running on localhost for local development mode
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Set by the RTK Query base query when an API call returns 401.
  const sessionHasExpired = useSelector((state: AppState) => state.auth.sessionExpired);

  // When `sessionHasExpired` is set, we
  // clear the stale MSAL tokens and let the render show the "Session expired" screen, where the
  // user re-authenticates.
  const recoverExpiredSession = React.useCallback(async () => {
    const activeAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

    if (activeAccount) {
      try {
        const response = await instance.acquireTokenSilent({ scopes: apiRequest.scopes, account: activeAccount });
        setAccessToken(response.idToken || response.accessToken);
        setTokenReady(true);
        dispatch(sessionRecovered());
        return;
      } catch (error) {
        // InteractionRequiredAuthError is the expected "session genuinely dead" signal.
        if (!(error instanceof InteractionRequiredAuthError)) {
          console.error('Unexpected error while re-verifying the session:', error);
        }
      }
    }

    console.warn('Session expired - clearing stale tokens; showing the Sign In screen');
    try {
      await instance.clearCache();
    } catch (error) {
      console.error('Failed to clear MSAL cache during session recovery:', error);
    } finally {
      setAccessToken(null);
      setTokenReady(false);
    }
  }, [instance, dispatch]);

  React.useEffect(() => {
    if (sessionHasExpired && !localMode) {
      recoverExpiredSession();
    }
  }, [sessionHasExpired, localMode, recoverExpiredSession]);

  // Automatically acquire token when user is authenticated
  React.useEffect(() => {
    // Skip Azure token acquisition if in local mode
    if (localMode) {
      return;
    }

    const acquireToken = async () => {
      if (accounts.length > 0 && !isLoading) {
        setLoadingMessage('Loading...');
        try {
          const request = {
            scopes: apiRequest.scopes,
            account: accounts[0],
          };

          const response = await instance.acquireTokenSilent(request);
          // For Azure AD B2C, try ID token first as it often has the right audience
          const token = response?.idToken || response?.accessToken;
          setAccessToken(token);
          setTokenReady(true);
          // Session is healthy — clear any pending "session expired" state.
          dispatch(sessionRecovered());
          console.log('Token stored in RTK Query storage (using', response.idToken ? 'ID token' : 'access token', ')');
        } catch (error) {
          console.error('Failed to acquire token silently:', error);

          // Authority mismatch (the configured authority changed) — clear cache and force a fresh login.
          if (error instanceof Error && error.message.includes('authority_mismatch')) {
            console.warn('Authority mismatch detected during silent acquisition - clearing cache and forcing re-login');
            await instance.clearCache();
            instance.loginRedirect(loginRequest).catch((loginError) => {
              console.error('Re-login failed after clearing cache:', loginError);
            });
            return;
          }

          // (refresh token expired, interaction required).
          // Flag it — the recovery effect clears stale tokens and the render shows the Sign In screen.
          if (error instanceof InteractionRequiredAuthError) {
            dispatch(sessionExpired());
            return;
          }

          setTokenReady(false);
        }
      } else if (accounts.length === 0) {
        console.log('No accounts available, clearing token');
        setAccessToken(null);
        setTokenReady(false);
      }
    };

    acquireToken();
  }, [accounts, isLoading, instance, localMode, dispatch]);

  // Notify unauthorized users (authenticated but not in the required group) that they are blocked.
  React.useEffect(() => {
    if (!localMode && accounts.length > 0 && tokenReady && !isAuthorized) {
      notifications.show({
        id: 'group-authorization-denied',
        color: 'red',
        title: 'Access Denied',
        message: 'Your account is not authorized to access this application. Please contact support to request access.',
        autoClose: false,
      });
    }
  }, [localMode, accounts.length, tokenReady, isAuthorized]);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((error) => {
      console.error('Login failed:', error);
    });
  };

  const handleLogout = () => {
    // Reset in-memory Redux state and clear persisted localStorage state
    dispatch(resetAntibodyAnalysis());
    dispatch(resetClonotypes());
    persistor.purge();

    // Check if in local mode
    if (localMode) {
      localStorage.removeItem('localMode');
      localStorage.removeItem('localUser');
      setLocalMode(false);
      setLocalUser(null);
      setAccessToken(null);
      return;
    }

    // Azure logout
    const logoutRequestWithHint = {
      ...logoutRequest,
      account,
      idTokenHint: account?.idToken, // Critical for Azure AD B2C logout
    };

    console.log('Logging out with parameters:', {
      account: account ? { username: account.username } : null,
      idTokenHint: account?.idToken ? 'Present' : 'Missing',
      postLogoutRedirectUri: logoutRequest.postLogoutRedirectUri,
    });

    instance.logoutRedirect(logoutRequestWithHint).catch((error) => {
      console.error('Logout failed:', error);
    });
  };

  const handleLocalSetup = () => {
    console.log('Local Setup clicked - bypassing Azure authentication');
    // Reset in-memory Redux state and clear persisted localStorage state
    dispatch(resetAntibodyAnalysis());
    dispatch(resetClonotypes());
    persistor.purge();
    // Persist local mode flag so it survives browser restarts
    const user = { name: 'Local Admin', username: 'admin' };
    localStorage.setItem('localMode', 'true');
    localStorage.setItem('localUser', JSON.stringify(user));
    // Set local mode with mock admin user
    setLocalMode(true);
    setLocalUser(user);
    // Set a mock token for API calls (backend should recognize this as local mode)
    setAccessToken('LOCAL_DEV_MODE');
    setTokenReady(true);
    console.log('Local mode activated - token set to LOCAL_DEV_MODE');
  };

  // Wait for the token before rendering children so RTK queries don't fire
  // unauthenticated and hit a 401. Both auth paths set the token asynchronously
  // (local mode via a mount effect, Azure via acquireTokenSilent), so both must
  // hold rendering until tokenReady.
  const waitingForLocalToken = localMode && !tokenReady;
  // Don't show the loader once the session has expired — render the Sign In screen instead.
  const waitingForAzureToken = !localMode && !sessionHasExpired && (isLoading || (accounts?.length > 0 && !tokenReady));
  if (waitingForLocalToken || waitingForAzureToken) {
    console.log('Showing loading spinner because inProgress =', inProgress, 'or tokenReady =', tokenReady);

    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="lg" />
        <Text mt="md">{loadingMessage}</Text>
      </Center>
    );
  }

  return (
    <>
      {/* Unauthenticated users see the login screen (unless in local mode) */}
      {!localMode && (
        <UnauthenticatedTemplate>
          <AppShell header={{ height: 60 }} padding="md">
            <AppShell.Header>
              <Group h="100%" px="md" justify="space-between" className="app-header">
                <Group>
                  <Image h={30} src={logoURL} alt="Bio Rad logo" />
                  <Text size="lg" fw={400} c="brand">
                    ImmunoSense
                  </Text>
                </Group>
                <Button onClick={handleLogin} variant="filled" color="brand">
                  Sign In
                </Button>
              </Group>
            </AppShell.Header>

            <AppShell.Main>
              <Center style={{ height: 'calc(100vh - 120px)' }}>
                <div style={{ textAlign: 'center', maxWidth: '500px' }}>
                  <Text size="xl" fw={500} mb="md">
                    Welcome to ImmunoSense
                  </Text>
                  <Text c="dimmed" mb="xl">
                    ImmunoSense is a tool for antibody sequence analysis. Please sign in with your registered Bio-Rad account to continue.
                  </Text>
                  <Group justify="center" gap="md">
                    <Button onClick={handleLogin} size="lg" variant="filled" color="brand">
                      Sign In with Bio-Rad
                    </Button>
                    {isLocalhost && (
                      <Button onClick={handleLocalSetup} size="lg" variant="outline" color="gray">
                        Local Setup
                      </Button>
                    )}
                  </Group>
                </div>
              </Center>
            </AppShell.Main>
          </AppShell>
        </UnauthenticatedTemplate>
      )}

      {/* Authenticated users see the main application — unless the session expired or they lack the group */}
      <AuthenticatedTemplate>
        {sessionHasExpired ? (
          <AppShell header={{ height: 60 }} padding="md">
            <AppShell.Header>
              <Group h="100%" px="md" className="app-header">
                <Image h={30} src={logoURL} alt="Bio Rad logo" />
                <Text size="lg" fw={400} c="brand">
                  ImmunoSense
                </Text>
              </Group>
            </AppShell.Header>

            <AppShell.Main>
              <Center style={{ height: 'calc(100vh - 120px)' }}>
                <Stack align="center" gap="md" style={{ maxWidth: '500px', textAlign: 'center' }}>
                  <Text size="xl" fw={500}>
                    Your session has expired
                  </Text>
                  <Text c="dimmed">For your security, please sign in again to continue.</Text>
                  <Button onClick={handleLogin} size="md" variant="filled" color="brand">
                    Sign In
                  </Button>
                </Stack>
              </Center>
            </AppShell.Main>
          </AppShell>
        ) : isAuthorized ? (
          <AppShell header={{ height: 60 }}>
            <AppShell.Header>
              <AppHeader userName={account?.name || account?.username} userEmail={String(account?.idTokenClaims?.email ?? '')} onLogout={handleLogout} />
            </AppShell.Header>

            <AppShell.Main>
              <Outlet />
            </AppShell.Main>
          </AppShell>
        ) : (
          <AppShell header={{ height: 60 }} padding="md">
            <AppShell.Header>
              <Group h="100%" px="md" className="app-header">
                <Image h={30} src={logoURL} alt="Bio Rad logo" />
                <Text size="lg" fw={400} c="brand">
                  ImmunoSense
                </Text>
              </Group>
            </AppShell.Header>

            <AppShell.Main>
              <Center style={{ height: 'calc(100vh - 120px)' }}>
                <Stack align="center" gap="md" style={{ maxWidth: '500px', textAlign: 'center' }}>
                  <Text size="xl" fw={500} c="red">
                    Access Denied
                  </Text>
                  <Text c="dimmed">
                    Your account is not authorized to access this application. Please contact support to request access, then sign in again.
                  </Text>
                  <Button onClick={handleLogout} size="md" variant="filled" color="brand">
                    Sign Out
                  </Button>
                </Stack>
              </Center>
            </AppShell.Main>
          </AppShell>
        )}
      </AuthenticatedTemplate>

      {/* Local mode users see the same app without Azure authentication */}
      {localMode && (
        <AppShell header={{ height: 60 }}>
          <AppShell.Header>
            <AppHeader userName={localUser?.name} userEmail={localUser?.username} onLogout={handleLogout} />
          </AppShell.Header>

          <AppShell.Main>
            <Outlet />
          </AppShell.Main>
        </AppShell>
      )}
    </>
  );
}
